import React from 'react';
import { 
  Award, 
  Users, 
  MapPin, 
  Clock, 
  Star, 
  Heart,
  Building,
  Shield,
  Coffee,
  Phone,
  Mail,
  Calendar,
  CheckCircle
} from 'lucide-react';

const AboutUs = () => {
  const milestones = [
    { year: "2010", title: "Hotel Founded", description: "Started our journey in Varanasi" },
    { year: "2013", title: "First Renovation", description: "Upgraded to premium facilities" },
    { year: "2016", title: "Award Recognition", description: "Received hospitality excellence award" },
    { year: "2019", title: "Major Expansion", description: "Added luxury suites and restaurant" },
    { year: "2023", title: "Current Status", description: "Top-rated hotel in Varanasi" }
  ];

  const values = [
    { icon: <Heart className="w-6 h-6" />, title: "Guest First", description: "Your comfort is our priority" },
    { icon: <Shield className="w-6 h-6" />, title: "Safety & Cleanliness", description: "Highest hygiene standards" },
    { icon: <Award className="w-6 h-6" />, title: "Excellence", description: "Award-winning service quality" },
    { icon: <Users className="w-6 h-6" />, title: "Team Spirit", description: "Dedicated professional staff" }
  ];

  const stats = [
    { icon: <Star className="w-5 h-5" />, value: "4.9/5", label: "Guest Rating" },
    { icon: <Users className="w-5 h-5" />, value: "10,000+", label: "Happy Guests" },
    { icon: <Building className="w-5 h-5" />, value: "50+", label: "Rooms & Suites" },
    { icon: <Coffee className="w-5 h-5" />, value: "15,000+", label: "Meals Served" },
    { icon: <Award className="w-5 h-5" />, value: "12+", label: "Awards Won" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-orange-50">
      
      {/* Hero Section */}
      <div className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/20 via-orange-900/10 to-amber-900/20"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-amber-300/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-300/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="w-10 h-1 bg-gradient-to-r from-amber-400 to-orange-400 rounded-full"></div>
            <span className="text-amber-700 font-semibold text-sm tracking-wider uppercase">Our Story</span>
            <div className="w-10 h-1 bg-gradient-to-r from-orange-400 to-amber-400 rounded-full"></div>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            About <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">Hotel Prakash Palace</span>
          </h1>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Where traditional Varanasi hospitality meets modern luxury, creating unforgettable experiences since 2010
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 pb-20">
        
        {/* Introduction Section */}
        <div className="grid lg:grid-cols-2 gap-12 mb-20 items-center">
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://catalog.wlimg.com/service-images/ttw/hotel-booking3.jpg"
                alt="Hotel Prakash Palace"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-amber-900/30 to-transparent"></div>
              
              {/* Floating Badges */}
              <div className="absolute top-6 left-6">
                <div className="bg-white/95 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg">
                  <div className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-amber-600" />
                    <span className="font-semibold text-gray-900">Award Winning</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute bottom-6 right-6">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-lg shadow-lg">
                </div>
              </div>
            </div>
          </div>
          
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Journey in Varanasi</h2>
            
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                Welcome to Hotel Prakash Palace, where every stay is a blend of traditional warmth and modern comfort. 
                Founded in 2010, we've been serving travelers with authentic Varanasi hospitality right at the heart 
                of the spiritual capital of India.
              </p>
              
              <p className="text-gray-700 leading-relaxed">
                Located just minutes from the sacred Assi Ghat, our hotel offers more than just accommodation - 
                we provide experiences that connect you with the soul of Varanasi while ensuring all modern 
                comforts and amenities.
              </p>
              
              <p className="text-gray-700 leading-relaxed">
                From business travelers seeking convenience to spiritual seekers looking for tranquility, 
                we cater to all with equal dedication and care.
              </p>
            </div>
            
            <div className="flex items-center gap-4 pt-4">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-amber-600" />
                <span className="text-gray-700 font-medium">Assi Ghat, Varanasi</span>
              </div>
              <div className="h-4 w-px bg-amber-200"></div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-600" />
                <span className="text-gray-700 font-medium">24/7 Service</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="bg-gradient-to-r from-amber-900 to-orange-800 rounded-2xl p-8 mb-16 shadow-2xl">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="inline-flex items-center gap-2 mb-2">
                  <div className="text-amber-300">
                    {stat.icon}
                  </div>
                  <div className="text-3xl font-bold text-white">{stat.value}</div>
                </div>
                <div className="text-amber-200 text-sm">{stat.label}</div>
                <div className="w-0 group-hover:w-full h-0.5 bg-amber-400 transition-all duration-300 mx-auto mt-2"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Milestones Timeline */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Our Journey Timeline</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From humble beginnings to becoming Varanasi's premier hospitality destination
            </p>
          </div>
          
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-amber-400 to-orange-400"></div>
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`relative flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  {/* Timeline Dot */}
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-8 h-8 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full border-4 border-white shadow-lg z-10"></div>
                  
                  {/* Content */}
                  <div className={`w-5/12 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8'}`}>
                    <div className="bg-white rounded-xl p-6 shadow-lg border border-amber-100 hover:shadow-xl transition-shadow duration-300">
                      <div className="text-2xl font-bold text-amber-700 mb-2">{milestone.year}</div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{milestone.title}</h3>
                      <p className="text-gray-600">{milestone.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Core Values */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Our Core Values</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Principles that guide our service and define our hospitality
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div key={index} className="group">
                <div className="bg-white rounded-xl p-6 border border-amber-100 hover:border-amber-300 hover:shadow-xl transition-all duration-300 h-full">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mb-5">
                    <div className="text-white">
                      {value.icon}
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                  
                  <div className="mt-6 pt-4 border-t border-amber-100 group-hover:border-amber-300 transition-colors">
                    <div className="flex items-center gap-2 text-amber-600">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Always Delivered</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-amber-900 to-orange-800 rounded-2xl p-10 text-center shadow-2xl">
          <h3 className="text-3xl font-bold text-white mb-4">
            Experience Our Hospitality
          </h3>
          <p className="text-amber-100 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied guests who have experienced the unique blend of traditional 
            warmth and modern comfort at Hotel Prakash Palace
          </p>
          
          <div className="flex flex-wrap justify-center gap-6">
            <button className="px-10 py-4 bg-white text-amber-900 rounded-xl font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center gap-3">
              <Calendar className="w-5 h-5" />
              Book Your Stay
            </button>
            <button className="px-10 py-4 bg-transparent text-white rounded-xl font-bold text-lg border-2 border-white/40 hover:bg-white/10 transition-all duration-300 flex items-center gap-3">
              <Phone className="w-5 h-5" />
              Contact Us
            </button>
          </div>
          
          <div className="mt-10 pt-6 border-t border-amber-700">
            <div className="flex flex-wrap justify-center gap-8 text-amber-200">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5" />
                <span>+91 9119641118</span>
              </div>
              <div className="h-4 w-px bg-amber-600"></div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5" />
                <span>hotelprakashpalacevns@gmail.com</span>
              </div>
              <div className="h-4 w-px bg-amber-600"></div>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5" />
                <span>Assi Ghat Road, Varanasi</span>
              </div>
            </div>
          </div>
        </div>

      </div>

    </div>
  );
};

export default AboutUs;